// src/components/CustomizeTable.js
import React, { useState, useEffect } from "react";
import { getData, postData } from "../../services/FetchNodeservices";
import {
  DragDropContext,
  Droppable,
  Draggable
} from "@hello-pangea/dnd";


/**
 * CustomizeTable
 * - Uses a multi-select dropdown for available fields
 * - Shows selected fields as draggable list to reorder
 * - Save persists to backend and dispatches `columnsUpdated` event for live sync
 */

export default function CustomizeTable() {
  const getUserId = () => {
    const userId = localStorage.getItem("user_id") || localStorage.getItem("userId");
    return userId && userId !== "undefined" && userId !== "null" ? userId : null;
  };

  const [userId, setUserId] = useState(getUserId());
  const [allColumns, setAllColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [pickerValue, setPickerValue] = useState("");

  useEffect(() => {
    const current = getUserId();
    setUserId(current);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await getData("job/columns");
        if (res.status && Array.isArray(res.data)) setAllColumns(res.data);
      } catch (err) {
        console.error("fetch all columns error:", err);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (!userId) return;
      try {
        const prefs = await getData(`job/preferences/${userId}`);
        if (prefs.status && Array.isArray(prefs.data)) {
          setSelectedColumns(prefs.data);
        }
      } catch (err) {
        console.error("fetch prefs:", err);
      }
    })();
  }, [userId]);

  const handleAdd = () => {
    if (!pickerValue) return;
    if (!selectedColumns.includes(pickerValue)) setSelectedColumns(prev => [...prev, pickerValue]);
    setPickerValue("");
  };

  const handleRemove = (key) => setSelectedColumns(prev => prev.filter(k => k !== key));

  const handleSave = async () => {
    if (!userId) { alert("Please login."); return; }
    try {
      const body = { columns: selectedColumns };
      const res = await postData(`job/preferences/${userId}`, body);
      if (res.status) {
        // dispatch live update so JobTable can pick it up
        window.dispatchEvent(new CustomEvent("columnsUpdated", { detail: selectedColumns }));
        alert("Preferences saved");
      } else {
        alert("Save failed");
      }
    } catch (err) {
      console.error(err);
      alert("Error saving preferences");
    }
  };

  // Drag and drop handlers
  const onDragEnd = (result) => {
    if (!result.destination) return;
    const list = Array.from(selectedColumns);
    const [moved] = list.splice(result.source.index, 1);
    list.splice(result.destination.index, 0, moved);
    setSelectedColumns(list);
  };

  // UI helpers
  const availableToSelect = allColumns.filter(c => !selectedColumns.includes(c.key));

  return (
    <div className="p-4 max-w-4xl mx-auto">
      <button
  onClick={() => window.location.href = "/dashboard/jobtable"}
  className="mb-4 px-4 py-2 bg-blue-600 text-white rounded-md shadow hover:bg-blue-700"
>
  ← Back to Table View
</button>

      <h2 className="text-2xl font-semibold mb-3">Customize Table</h2>
      {!userId && (
        <div className="p-4 bg-yellow-50 border rounded mb-4">
          <p className="text-sm">Please login to save preferences. Detected localStorage keys: {Object.keys(localStorage).join(", ")}</p>
        </div>
      )}

      <div className="mb-4 flex gap-3 items-start">
        <select className="border px-3 py-2 rounded flex-1" value={pickerValue} onChange={(e) => setPickerValue(e.target.value)}>
          <option value="">Select field to add</option>
          {availableToSelect.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <button onClick={handleAdd} className="px-4 py-2 bg-blue-600 text-white rounded">Add</button>
        <button onClick={handleSave} className="px-4 py-2 bg-green-600 text-white rounded">Save</button>
      </div>

      <div className="mb-4">
        <h3 className="font-medium mb-2">Selected Fields (drag to reorder)</h3>

        <div className="bg-white border rounded p-3">
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="selected-columns">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                  {selectedColumns.length === 0 && <div className="text-sm text-gray-500">No fields selected</div>}
                  {selectedColumns.map((key, idx) => {
                    const meta = allColumns.find(c => c.key === key) || { label: key, key };
                    return (
                      <Draggable key={key} draggableId={key} index={idx}>
                        {(dr) => (
                          <div ref={dr.innerRef} {...dr.draggableProps} {...dr.dragHandleProps} className="flex items-center justify-between p-2 border rounded">
                            <div className="flex items-center gap-3">
                              <div className="w-6 h-6 bg-gray-200 rounded flex items-center justify-center text-xs">≡</div>
                              <div className="text-sm font-medium">{meta.label}</div>
                            </div>
                            <div className="flex gap-2 items-center">
                              <button onClick={() => handleRemove(key)} className="px-2 py-1 text-sm border rounded">Remove</button>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      </div>

      <div className="mt-4 bg-gray-50 p-3 rounded text-sm">
        <strong>Preview:</strong> {selectedColumns.join(", ") || "None"}
      </div>
    </div>
  );
}
